import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiUrl, postCsv } from "../api/client";
import { STATUS_LABEL, SETTABLE_STATUSES, type ActionPatch, type ActionStatus, type BacklogRow, type BacklogSummary } from "../api/remediation";
import { riskLevelColor } from "../lib/riskVocabulary";
import { useAuth } from "../auth/AuthContext";

/**
 * Remediation backlog (Sprint 3). Actions are created from each completed
 * run's findings; people own status, owner, due date and notes. A new run
 * closes actions whose finding is gone (Verified resolved) and reopens Done
 * actions whose finding persists.
 */

interface Member { user_id: string; display_name: string; status?: string }

const STATUS_COLOR: Record<ActionStatus, string> = {
  Proposed: "#64748b",
  Accepted: "#0B1F3A",
  In_progress: "#2563eb",
  Done: "#0f766e",
  Verified_resolved: riskLevelColor("Very_low"),
  Risk_accepted: "#a16207",
  Not_applicable: "#94a3b8",
};

// The same wave names as the reports and the cockpit.
const WAVE_LABEL: Record<number, string> = { 1: "Wave 1 · Act now (P1)", 2: "Wave 2 · Plan migration (P2)", 3: "Wave 3 · Strengthen and Monitor (P3–4)" };

export function RemediationPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const auth = useAuth();
  const canEdit = auth.hasRole("admin", "super_user", "analyst");
  const [wave, setWave] = useState<number | null>(null);
  const [status, setStatus] = useState<"open" | "all" | ActionStatus>("open");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<BacklogRow | null>(null);

  const rows = useQuery({ queryKey: ["remediation", projectId], queryFn: () => api.get<BacklogRow[]>(`/projects/${projectId}/remediation`) });
  const summary = useQuery({ queryKey: ["remediation-summary", projectId], queryFn: () => api.get<BacklogSummary>(`/projects/${projectId}/remediation/summary`) });
  const members = useQuery({ queryKey: ["project-members", projectId], queryFn: () => api.get<Member[]>(`/projects/${projectId}/members`), enabled: canEdit });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ActionPatch }) => api.patch(`/remediation/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["remediation", projectId] });
      qc.invalidateQueries({ queryKey: ["remediation-summary", projectId] });
    },
  });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (rows.data ?? []).filter((r) =>
      (wave === null || r.wave === wave) &&
      (status === "all" || (status === "open" ? ["Proposed", "Accepted", "In_progress"].includes(r.status) : r.status === status)) &&
      (!s || r.asset_name.toLowerCase().includes(s) || r.title.toLowerCase().includes(s) || r.business_unit.toLowerCase().includes(s)));
  }, [rows.data, wave, status, search]);

  if (rows.isLoading) return <div className="p-8 text-sm text-ink-500 italic">Loading backlog…</div>;
  const sm = summary.data;

  return (
    <div className="p-6 max-w-7xl space-y-5" data-testid="remediation-panel">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl text-ink-900">Remediation backlog</h2>
          <p className="text-xs text-ink-500 mt-1">Generated from each assessment run. The next run verifies what was fixed and reopens what was not.</p>
        </div>
        <div className="flex gap-2">
          <a href={apiUrl(`/projects/${projectId}/remediation.csv`)} className="text-xs border border-ink-200 rounded-sm px-3 py-1.5 text-ink-700 hover:text-accent whitespace-nowrap">Export CSV (Excel)</a>
          {/* Open actions without a ticket, as tracker import files (roadmap R1). */}
          <a href={apiUrl(`/projects/${projectId}/remediation/tickets.csv?system=jira`)} title="Open actions without a ticket, for Jira's CSV importer" className="text-xs border border-ink-200 rounded-sm px-3 py-1.5 text-ink-700 hover:text-accent whitespace-nowrap">Jira tickets</a>
          <a href={apiUrl(`/projects/${projectId}/remediation/tickets.csv?system=servicenow`)} title="Open actions without a ticket, for a ServiceNow import set" className="text-xs border border-ink-200 rounded-sm px-3 py-1.5 text-ink-700 hover:text-accent whitespace-nowrap">ServiceNow tickets</a>
          <TicketStatusImport projectId={projectId} />
        </div>
      </header>

      {sm?.by_owner && sm.by_owner.length > 0 && (
        <details className="bg-white border border-ink-100 rounded-sm px-4 py-2 text-xs" data-testid="progress-by-owner">
          <summary className="cursor-pointer text-ink-700">Progress by owner team{sm.ticketed ? ` · ${sm.ticketed} with a ticket, ${sm.ticketed_done ?? 0} done in the tracker` : ""}</summary>
          <table className="mt-2 w-full tabular-nums">
            <thead><tr className="text-ink-500 text-left"><th className="font-normal">Owner team</th><th className="font-normal text-right">Open</th><th className="font-normal text-right">Closed</th><th className="font-normal text-right">Overdue</th></tr></thead>
            <tbody>{sm.by_owner.slice(0, 15).map((o) => (
              <tr key={o.owner}><td>{o.owner}</td><td className="text-right">{o.open}</td><td className="text-right">{o.done}</td><td className="text-right" style={o.overdue ? { color: riskLevelColor("Critical") } : undefined}>{o.overdue}</td></tr>
            ))}</tbody>
          </table>
        </details>
      )}
      {sm && (
        <div className="grid grid-cols-4 gap-3">
          {sm.by_wave.map((w) => (
            <button key={w.wave} type="button" onClick={() => setWave(wave === w.wave ? null : w.wave)}
              className={`text-left bg-white border rounded-sm px-4 py-3 ${wave === w.wave ? "border-accent" : "border-ink-100"}`} data-testid={`wave-card-${w.wave}`}>
              <div className="text-[10px] uppercase tracking-wider text-ink-500">{WAVE_LABEL[w.wave]}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">{w.open}<span className="text-xs font-normal text-ink-500"> open of {w.total}</span></div>
              <div className="flex gap-3 text-[11px] mt-1">
                <span className="text-ink-500">{w.done} closed</span>
                {w.overdue > 0 && <span style={{ color: riskLevelColor("Critical") }}>{w.overdue} overdue</span>}
                {w.unassigned > 0 && <span style={{ color: riskLevelColor("High") }}>{w.unassigned} unassigned</span>}
              </div>
            </button>
          ))}
          <div className="bg-white border border-ink-100 rounded-sm px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-500">By status</div>
            <ul className="mt-1 text-[11px] space-y-0.5">
              {Object.entries(sm.by_status).map(([s, n]) => (
                <li key={s} className="flex justify-between"><span style={{ color: STATUS_COLOR[s as ActionStatus] }}>{STATUS_LABEL[s as ActionStatus] ?? s}</span><span className="tabular-nums">{n}</span></li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs">
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label="Status filter">
          <option value="open">Open</option>
          <option value="all">All statuses</option>
          {(Object.keys(STATUS_LABEL) as ActionStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <input type="search" placeholder="Search asset, action or business unit" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72" />
        {wave !== null && <button type="button" className="text-accent" onClick={() => setWave(null)}>Clear wave filter</button>}
        <span className="ml-auto text-ink-500">{filtered.length} actions</span>
      </div>

      {(rows.data ?? []).length === 0 ? (
        <p className="text-sm text-ink-500 bg-white border border-ink-100 rounded-sm p-6">No actions yet. Run an assessment to generate the backlog.</p>
      ) : (
        <div className="bg-white border border-ink-100 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="backlog-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-ink-500 text-left border-b border-ink-100">
                <th className="px-3 py-2">#</th><th className="px-2 py-2">Asset</th><th className="px-2 py-2">Action</th>
                <th className="px-2 py-2">Wave</th><th className="px-2 py-2">Assignee</th><th className="px-2 py-2">Due</th>
                <th className="px-2 py-2">Status</th><th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-ink-50" data-testid={`action-${r.id}`}>
                  <td className="px-3 py-1.5 tabular-nums text-ink-400">{r.priority}</td>
                  <td className="px-2 py-1.5"><div className="font-medium text-ink-900">{r.asset_name}</div><div className="text-[10.5px] text-ink-500">{r.business_unit}</div></td>
                  <td className="px-2 py-1.5 text-ink-800 max-w-[300px]" title={r.rationale ?? undefined}>{r.title}</td>
                  <td className="px-2 py-1.5 font-semibold" style={{ color: r.wave === 1 ? riskLevelColor("Critical") : "#0B1F3A" }}>W{r.wave}</td>
                  {/* The action's assignee; the asset owner is context, not an assignment (C-020). */}
                  <td className="px-2 py-1.5">{r.owner_name ?? <><span style={{ color: riskLevelColor("High") }}>Unassigned</span>{r.owner_text && <span className="block text-[10px] italic text-ink-500">Asset owner: {r.owner_text}</span>}</>}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap tabular-nums" style={{ color: r.overdue ? riskLevelColor("Critical") : undefined }}>
                    {r.due_date ?? r.target_date ?? "—"}{r.overdue && " · overdue"}{!r.due_date && r.target_date && <span className="text-ink-400"> (target)</span>}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className="inline-block px-1.5 py-0.5 rounded-sm text-[10.5px] font-medium" style={{ color: STATUS_COLOR[r.status], background: `${STATUS_COLOR[r.status]}14` }}>{STATUS_LABEL[r.status]}</span>
                    {r.status === "Risk_accepted" && r.risk_accepted_until && <span className="text-[10px] text-ink-500"> until {r.risk_accepted_until}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right">{canEdit && <button type="button" className="text-accent hover:underline" onClick={() => setEditing(r)}>Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditAction row={editing} members={members.data ?? []} saving={patch.isPending} error={patch.error as Error | null}
          onCancel={() => { setEditing(null); patch.reset(); }}
          onSave={(body) => patch.mutate({ id: editing.id, body }, { onSuccess: () => setEditing(null) })} />
      )}
    </div>
  );
}

function EditAction({ row, members, saving, error, onCancel, onSave }: { row: BacklogRow; members: Member[]; saving: boolean; error: Error | null; onCancel: () => void; onSave: (b: ActionPatch) => void }) {
  const [status, setStatus] = useState<ActionStatus>(row.status);
  const [owner, setOwner] = useState(row.owner_user_id ?? "");
  const [due, setDue] = useState(row.due_date ?? "");
  const [until, setUntil] = useState(row.risk_accepted_until ?? "");
  const [notes, setNotes] = useState(row.notes ?? "");
  const needsUntil = status === "Risk_accepted" && !until;
  const submit = () => {
    const body: ActionPatch = {};
    if (status !== row.status && status !== "Verified_resolved") body.status = status;
    if ((owner || null) !== row.owner_user_id) body.owner_user_id = owner || null;
    if ((due || null) !== row.due_date) body.due_date = due || null;
    if (status === "Risk_accepted" && until !== row.risk_accepted_until) body.risk_accepted_until = until || null;
    if (status === "Risk_accepted" && body.status === "Risk_accepted" && !body.risk_accepted_until) body.risk_accepted_until = until;
    if ((notes || null) !== row.notes) body.notes = notes || null;
    onSave(body);
  };
  return (
    <div className="fixed inset-0 z-40 bg-ink-900/30 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-md shadow-lg max-w-lg w-full p-6 space-y-3 text-sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Edit action">
        <div>
          <h3 className="font-medium text-ink-900">{row.title}</h3>
          <p className="text-xs text-ink-500">{row.asset_name} · {row.business_unit} · W{row.wave}</p>
        </div>
        <label className="block text-xs text-ink-600">Status
          <select className="block w-full mt-1" value={status} onChange={(e) => setStatus(e.target.value as ActionStatus)}>
            {row.status === "Verified_resolved" && <option value="Verified_resolved">{STATUS_LABEL.Verified_resolved}</option>}
            {SETTABLE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        {status === "Risk_accepted" && (
          <label className="block text-xs text-ink-600">Risk accepted until (required)
            <input type="date" className="block w-full mt-1" value={until} onChange={(e) => setUntil(e.target.value)} />
          </label>
        )}
        <label className="block text-xs text-ink-600">Owner
          <select className="block w-full mt-1" value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">— Unassigned{row.owner_text ? ` (asset owner role: ${row.owner_text})` : ""}</option>
            {members.filter((m) => m.status !== "Disabled").map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}
          </select>
        </label>
        <label className="block text-xs text-ink-600">Due date <span className="text-ink-400" title="The wave's due date (fix-now actions: 90 days from the run, or the wave's due date if earlier); overdue is tracked against it, never against a wave's start date">(target {row.target_date ?? "—"})</span>
          <input type="date" className="block w-full mt-1" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
        <label className="block text-xs text-ink-600">Notes
          <textarea className="block w-full mt-1 h-20" value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
        </label>
        {error && <p className="text-xs text-accent">{error.message}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn-primary" disabled={saving || needsUntil} onClick={submit}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

interface TicketImportResult { rows: number; matched: number; linked: number; status_changed: number; not_in_project: number; no_qram_id: number; unknown_status: string[] }

/** Reads a Jira or ServiceNow export back: ticket keys and statuses onto the actions (roadmap R1). */
function TicketStatusImport({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [system, setSystem] = useState<"jira" | "servicenow">("jira");
  const [msg, setMsg] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async (file: File) => postCsv<TicketImportResult>(`/projects/${projectId}/remediation/tickets/import?system=${system}`, await file.text()),
    onSuccess: (r) => {
      setMsg(`${r.matched} of ${r.rows} tickets matched; ${r.linked} newly linked; ${r.status_changed} statuses updated${r.not_in_project ? `; ${r.not_in_project} not from this project` : ""}${r.unknown_status.length ? `; statuses not recognised: ${r.unknown_status.join(", ")}` : ""}.`);
      qc.invalidateQueries({ queryKey: ["remediation", projectId] });
      qc.invalidateQueries({ queryKey: ["remediation-summary", projectId] });
    },
    onError: (e) => setMsg(e instanceof Error ? e.message : "Import failed"),
  });
  return (
    <span className="flex items-center gap-1 text-xs" title="Upload the tracker's CSV export to record ticket keys and statuses">
      <select aria-label="Tracker" value={system} onChange={(e) => setSystem(e.target.value as "jira" | "servicenow")} className="border border-ink-200 rounded-sm px-1 py-1">
        <option value="jira">Jira</option><option value="servicenow">ServiceNow</option>
      </select>
      <label className="border border-ink-200 rounded-sm px-3 py-1.5 text-ink-700 hover:text-accent cursor-pointer whitespace-nowrap">
        {run.isPending ? "Reading…" : "Import ticket statuses"}
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) run.mutate(f); e.target.value = ""; }} />
      </label>
      {msg && <span role="status" className="text-ink-500 max-w-xs">{msg}</span>}
    </span>
  );
}
